import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  pickWeightedKey,
  recordAttempt,
  statsFor,
} from "@nav-games/games-core";
import {
  NUMERIC_PREFIX_POSE,
  SEMAPHORE_CODE,
  SEMAPHORE_NUMERALS,
} from "@nav-games/semaphore";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { SemaphoreFigure } from "../../ui/SemaphoreFigure";

interface NumeralsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "semaphore-numerals";
const GAME = "numerals";
const CARDS_PER_SESSION = 15;
const BEAT_THE_CLOCK_MS = 60_000;
const CHOICES_PER_CARD = 5;
const PREFIX_MS = 800;
const DIGIT_MS = 1400;

const DIGITS = "0123456789".split("");

// Recent answers kept out of the next draw so the spaced-repetition
// picker doesn't resurface the same card within a short window.
// Clamped to pool size - 1 so there is always an eligible card.
const AVOID_WINDOW = Math.min(8, DIGITS.length - 1);

type SessionMode = "cards" | "timed";

interface Card {
  digit: string;
  letter: string;
  choices: readonly string[];
}

function nextCard(recent: readonly string[]): Card {
  const digit = pickWeightedKey(MODULE, DIGITS, recent);
  const letter = SEMAPHORE_NUMERALS[digit]!;
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = DIGITS[Math.floor(Math.random() * DIGITS.length)]!;
    if (d !== digit) distractors.add(d);
  }
  const choices = shuffle([digit, ...distractors]);
  return { digit, letter, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Numerals({ onNavigate }: NumeralsProps) {
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  if (!sessionMode) {
    return (
      <Picker
        onStart={setSessionMode}
        onBack={() => onNavigate("/play/semaphore")}
      />
    );
  }
  return (
    <Game
      sessionMode={sessionMode}
      onAgain={() => setSessionMode(sessionMode)}
      onBack={() => onNavigate("/play/semaphore")}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (m: SessionMode) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Semaphore numerals</h2>
      <p className="muted">
        Numbers in semaphore are sent by first signalling the{" "}
        <strong>numeric prefix</strong> pose (the same as letter J), then
        each digit is signalled with a letter pose A–I, K. Each round
        plays the prefix then the digit pose; tap the digit it represents.
      </p>
      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("cards")}>
            {CARDS_PER_SESSION} numbers
          </button>
          <button className="secondary" onClick={() => onStart("timed")}>
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Semaphore
      </button>
    </>
  );
}

interface GameProps {
  sessionMode: SessionMode;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ sessionMode, onAgain, onBack }: GameProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode],
  );

  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard([]);
    recentRef.current = [c.digit];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  // 3-phase animation: prefix → digit-pose → choices interactive.
  // `phase` drives which figure shows above the buttons. Buttons are
  // disabled until phase reaches `answer`.
  type Phase = "prefix" | "digit" | "answer";
  const [phase, setPhase] = useState<Phase>("prefix");

  // Re-run the animation cycle on each new card.
  useEffect(() => {
    setPhase("prefix");
    const t1 = setTimeout(() => setPhase("digit"), PREFIX_MS);
    const t2 = setTimeout(() => setPhase("answer"), PREFIX_MS + DIGIT_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [card]);

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(recentRef.current);
    recentRef.current = [...recentRef.current, next.digit].slice(-AVOID_WINDOW);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [index, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result || phase !== "answer") return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.digit;
      const attempt: Omit<Attempt, "module"> = {
        char: card.digit,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 400 : 900);
    },
    [advance, card, feedback, phase, recorder, result, shownAt],
  );

  if (result) {
    return <EndCard result={result} onAgain={onAgain} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Number ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const figurePose =
    phase === "prefix" ? NUMERIC_PREFIX_POSE : SEMAPHORE_CODE[card.letter]!;
  const phaseLabel =
    phase === "prefix"
      ? "Numbers follow…"
      : phase === "digit"
        ? "(in number mode)"
        : "Pick the digit";

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "semaphore" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <SemaphoreFigure pose={figurePose} size={180} />
        <p
          className="small muted"
          style={{ marginTop: 8, minHeight: "1.2em" }}
        >
          {phaseLabel}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 8,
            marginTop: 8,
          }}
        >
          {card.choices.map((c) => (
            <button
              key={c}
              className="secondary"
              disabled={!!feedback || phase !== "answer"}
              onClick={() => onChoose(c)}
              style={{
                fontSize: 20,
                padding: "12px 0",
                background:
                  feedback && c === card.digit
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  result,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  onAgain: () => void;
  onBack: () => void;
}) {
  const rolling = DIGITS
    .map((c) => ({ char: c, ...statsFor(MODULE, c) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}.
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest digits (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.char}>
                <code>{w.char}</code> = letter pose <code>{SEMAPHORE_NUMERALS[w.char]}</code> —{" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Semaphore
        </button>
      </div>
    </>
  );
}
